import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, Input } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { FieldRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import useCallbackRef from 'hooks/useCallbackRef';
import useElementWidth from 'hooks/useElementWidth';
import SearchIcon from 'icons/Search.icon';
import SortIcon from 'icons/Sort.icon';
import {
  getHasElementsInArray,
  includesIgnoreCase,
  isEmail,
  normalizeText,
} from 'utils';
import { EMPTY_ARRAY } from 'data/global';

import './RfiAssigneeCombobox.styles.scss';

type RfiAssigneeComboboxProps = FieldRenderProps<string> & {
  className?: string;
  people: string[];
};

const RfiAssigneeCombobox: React.FC<RfiAssigneeComboboxProps> = (props) => {
  const { className, input, people = EMPTY_ARRAY } = props;

  const { value, onChange } = input;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();
  const [search, setSearch] = useState('');
  const [triggerEl, triggerRef] = useCallbackRef<HTMLDivElement>();

  const triggerWidth = useElementWidth(triggerEl);

  const normalizedSearch = normalizeText(search);

  const { t } = useTranslation();

  const filteredPeople = useMemo(
    () => people.filter((email) => includesIgnoreCase(email, normalizedSearch)),
    [people, normalizedSearch],
  );

  const isAlreadyListed = useMemo(
    () => people.some((email) => normalizeText(email) === normalizedSearch),
    [people, normalizedSearch],
  );

  const showUseEmailOption = useMemo(
    () => !!normalizedSearch && isEmail(normalizedSearch) && !isAlreadyListed,
    [isAlreadyListed, normalizedSearch],
  );

  const handleSelect = useCallback(
    (email: string) => {
      setSearch('');
      onChange(email);
      dropdown?.setIsOpen(false);
    },
    [dropdown, onChange],
  );

  const classes = classNames('rfi-assignee-combobox', className);

  return (
    <Dropdown
      ref={dropdownRef}
      className={classes}
      closeWithJS
      renderAsPortal
      trigger={
        <div ref={triggerRef}>
          <Input
            placeholder={t('assignToSearchPlaceholder')}
            suffixNode={
              <span className="rfi-assignee-combobox__sort-icon">
                <SortIcon />
                <SortIcon />
              </span>
            }
            value={value || ''}
            readOnly
          />
        </div>
      }
    >
      <div
        className="rfi-assignee-combobox__options"
        style={{ width: triggerWidth }}
      >
        <Input
          className="rfi-assignee-combobox__people-search"
          placeholder={t('searchPeople')}
          prefixNode={<SearchIcon />}
          value={search}
          autoComplete="off"
          onChange={({ target: { value } }) => setSearch(value)}
        />

        {filteredPeople.map((email) => (
          <div
            key={email}
            className={classNames('rfi-assignee-combobox__option', {
              selected: email === value,
            })}
            onClick={() => handleSelect(email)}
          >
            {email}
          </div>
        ))}

        {showUseEmailOption && (
          <div
            className="rfi-assignee-combobox__option"
            onClick={() => handleSelect(normalizedSearch)}
          >
            {t('useEmailOption', { email: normalizedSearch })}
          </div>
        )}

        {!getHasElementsInArray(filteredPeople) && !showUseEmailOption && (
          <div className="rfi-assignee-combobox__empty">
            {t('noMatchesFound')}
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default RfiAssigneeCombobox;
